import { DialogContentText } from '@mui/material'
import { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import ConfirmPopup from './ConfirmPopup'

export interface IConfirmationDialog {
    DialogHeaderText: string
    DialogBodyText: ReactNode
    openDialog: boolean
    handleComfirm: () => void
    handleClose: () => void
    confirmtext?: string
    cancelText?: string
    /**
     * UBQ-5212: stable, locale-independent ids for the action buttons.
     *
     * <p>Without these the button id falls back to the button's own label, so passing a translated
     * `confirmtext` produces an id that changes with the language ("Send" / "Senden" / "送信"). The E2E
     * suite locates by id, so a localized id is not addressable. Callers that pass `confirmtext` should
     * pass these too; omitting them keeps the previous behaviour, which existing callers rely on
     * (`id="delete"` comes from the default branch and is already locale-stable).
     */
    confirmId?: string
    cancelId?: string
    /** rules around the body; off by default since most of these are a one-line "Delete X?" */
    dividers?: boolean
}

/**
 * The original confirm popup's API, kept so its callers don't change. It renders through the shared
 * `ConfirmPopup` (UBQ-5808); new code should use that directly. Button ids and titles stay as they
 * were because the E2E suite locates this popup by `@id='delete'` and `@title='Delete'`.
 */
const ConfirmationDialog = (props: IConfirmationDialog): ReactElement => {
    const { t } = useTranslation()
    const {
        openDialog,
        handleComfirm,
        handleClose,
        DialogHeaderText,
        DialogBodyText,
        confirmtext,
        cancelText,
        confirmId,
        cancelId,
        dividers = false,
    } = props
    const confirmLabel = confirmtext ? confirmtext : t('Delete')
    const cancelLabel = cancelText ? cancelText : t('cancel')
    return (
        <ConfirmPopup
            open={openDialog}
            onClose={handleClose}
            onConfirm={() => handleComfirm()}
            title={DialogHeaderText}
            /* Sized to its content, as before, rather than the shared popup's full width. */
            fullWidth={false}
            dividers={dividers}
            confirmLabel={confirmLabel}
            cancelLabel={cancelLabel}
            confirmButtonProps={{
                id: confirmId ?? `${confirmtext || 'delete'}`,
                title: confirmLabel,
                autoFocus: true,
            }}
            cancelButtonProps={{
                id: cancelId ?? `${cancelText || 'cancel'}`,
                title: cancelLabel,
            }}
        >
            <DialogContentText component="div">
                {DialogBodyText}
            </DialogContentText>
        </ConfirmPopup>
    )
}

export default ConfirmationDialog
